<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-info">
      <b-row class="align-items-center">
        <b-col cols="12">
          <h6 class="h2 text-white d-inline-block mb-0">
            <i class="fa fa-cogs mr-2"></i>Pengaturan Sistem
          </h6>
          <p class="text-white-50 mb-0 mt-1">Backup, cache, dan info sistem</p>
        </b-col>
      </b-row>

      <b-row>
        <b-col xl="3" md="6">
          <stats-card
            title="Database Size"
            :sub-title="info.db_size_mb + ' MB'"
            type="gradient-red"
            icon="fa fa-database"
          >
            <template slot="footer"><span>{{ info.table_count }} tabel</span></template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Storage"
            :sub-title="info.storage_size_mb + ' MB'"
            type="gradient-orange"
            icon="fa fa-hdd"
          >
            <template slot="footer"><span>Files & uploads</span></template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="PHP Version"
            :sub-title="info.php_version"
            type="gradient-info"
            icon="fa fa-code"
          >
            <template slot="footer"><span>Laravel {{ info.laravel_version }}</span></template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Cache Driver"
            :sub-title="info.cache_driver"
            type="gradient-primary"
            icon="fa fa-bolt"
          >
            <template slot="footer"><span>Env: {{ info.app_env }}</span></template>
          </stats-card>
        </b-col>
      </b-row>
    </base-header>

    <b-container fluid class="mt--6">
      <!-- Backup Section -->
      <b-row>
        <b-col lg="12">
          <b-card no-body>
            <b-card-header class="bg-transparent">
              <b-row align-v="center">
                <b-col>
                  <h3 class="mb-0">
                    <i class="fa fa-shield-alt text-success mr-2"></i>Backup Database
                  </h3>
                  <p class="text-muted mb-0" style="font-size:0.85rem;">
                    Backup penuh database dalam format .sql.gz (terkompresi)
                  </p>
                </b-col>
                <b-col class="text-right">
                  <b-button variant="success" size="sm" @click="createBackup" :disabled="backingUp">
                    <i class="fa fa-plus mr-1" v-if="!backingUp"></i>
                    <i class="fa fa-spinner fa-spin mr-1" v-else></i>
                    Buat Backup
                  </b-button>
                </b-col>
              </b-row>
            </b-card-header>
            <b-card-body>
              <div v-if="!info.backups || !info.backups.length" class="text-center py-4 text-muted">
                <i class="fa fa-folder-open fa-2x mb-2"></i>
                <p class="mb-0">Belum ada backup</p>
              </div>
              <b-table v-else :items="info.backups" :fields="backupFields" hover small>
                <template #cell(filename)="data">
                  <code style="font-size:0.82rem;">{{ data.value }}</code>
                </template>
                <template #cell(size_mb)="data">
                  <b-badge variant="info">{{ data.value }} MB</b-badge>
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="primary" @click="downloadBackup(data.item.filename)" class="mr-1">
                    <i class="fa fa-download"></i>
                  </b-button>
                  <b-button size="sm" variant="danger" @click="deleteBackup(data.item.filename)">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Cache Section -->
      <b-row class="mt-4">
        <b-col lg="12">
          <b-card no-body>
            <b-card-header class="bg-transparent">
              <b-row align-v="center">
                <b-col>
                  <h3 class="mb-0">
                    <i class="fa fa-broom text-warning mr-2"></i>Manajemen Cache
                  </h3>
                  <p class="text-muted mb-0" style="font-size:0.85rem;">
                    Bersihkan cache aplikasi (cache, config, route, view)
                  </p>
                </b-col>
                <b-col class="text-right">
                  <b-button variant="warning" size="sm" @click="clearCache">
                    <i class="fa fa-broom mr-1"></i>Clear All Cache
                  </b-button>
                </b-col>
              </b-row>
            </b-card-header>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import BaseHeader from "@/components/admin/BaseHeader";
import axios from "axios";
import authHeader from "@/services/auth-header";
import { baseURL } from "@/constants";

export default {
  name: "SystemSettings",
  components: { BaseHeader },
  data: function () {
    return {
      info: {
        db_size_mb: 0,
        table_count: 0,
        storage_size_mb: 0,
        php_version: "",
        laravel_version: "",
        cache_driver: "",
        app_env: "",
        backups: []
      },
      backingUp: false,
      backupFields: [
        { key: "filename", label: "Nama File" },
        { key: "size_mb", label: "Ukuran" },
        { key: "created_at", label: "Tanggal", sortable: true },
        { key: "actions", label: "Aksi", thClass: "text-right", tdClass: "text-right" }
      ]
    };
  },
  created: function () {
    this.loadInfo();
  },
  methods: {
    loadInfo: function () {
      var self = this;
      self.$store.commit("SHOW_LOADING", "Memuat informasi sistem...");
      axios
        .get(baseURL + "/system/info", { headers: authHeader() })
        .then(function (res) {
          self.info = res.data;
          self.$store.commit("HIDE_LOADING");
        })
        .catch(function () {
          self.$store.commit("HIDE_LOADING");
        });
    },
    createBackup: function () {
      var self = this;
      self.$swal({
        title: "Buat backup database?",
        text: "Proses ini bisa memakan waktu tergantung ukuran DB",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Ya, buat backup",
        cancelButtonText: "Batal"
      }).then(function (result) {
        if (!result.value) return;
        self.backingUp = true;
        self.$store.commit("SHOW_LOADING", "Membuat backup database...");
        axios
          .post(baseURL + "/system/backup", {}, { headers: authHeader(), timeout: 600000 })
          .then(function (res) {
            self.backingUp = false;
            self.$store.commit("HIDE_LOADING");
            self.$swal({
              icon: "success",
              title: "Backup berhasil",
              text: res.data.filename + " (" + res.data.size_mb + " MB)"
            });
            self.loadInfo();
          })
          .catch(function (error) {
            self.backingUp = false;
            self.$store.commit("HIDE_LOADING");
            self.$swal({
              icon: "error",
              title: "Backup gagal",
              text: (error.response && error.response.data && error.response.data.message) || "Server error"
            });
          });
      });
    },
    downloadBackup: function (filename) {
      var self = this;
      self.$store.commit("SHOW_LOADING", "Menyiapkan unduhan...");
      axios
        .get(baseURL + "/system/backup/" + encodeURIComponent(filename) + "/download", {
          headers: authHeader(),
          responseType: "blob"
        })
        .then(function (response) {
          var url = window.URL.createObjectURL(new Blob([response.data]));
          var link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          self.$store.commit("HIDE_LOADING");
        })
        .catch(function () {
          self.$store.commit("HIDE_LOADING");
          self.$swal({ icon: "error", title: "Gagal mengunduh" });
        });
    },
    deleteBackup: function (filename) {
      var self = this;
      self.$swal({
        title: "Hapus backup?",
        text: filename,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal"
      }).then(function (result) {
        if (!result.value) return;
        axios
          .delete(baseURL + "/system/backup/" + encodeURIComponent(filename), { headers: authHeader() })
          .then(function () {
            self.$swal({ icon: "success", title: "Backup dihapus", timer: 1500 });
            self.loadInfo();
          })
          .catch(function () {
            self.$swal({ icon: "error", title: "Gagal menghapus" });
          });
      });
    },
    clearCache: function () {
      var self = this;
      self.$swal({
        title: "Bersihkan semua cache?",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Ya",
        cancelButtonText: "Batal"
      }).then(function (result) {
        if (!result.value) return;
        self.$store.commit("SHOW_LOADING", "Membersihkan cache...");
        axios
          .post(baseURL + "/system/cache/clear", {}, { headers: authHeader() })
          .then(function () {
            self.$store.commit("HIDE_LOADING");
            self.$swal({ icon: "success", title: "Cache dibersihkan", timer: 1500 });
          })
          .catch(function () {
            self.$store.commit("HIDE_LOADING");
            self.$swal({ icon: "error", title: "Gagal" });
          });
      });
    }
  }
};
</script>

